import { BLOCK_LANG, BUFFER_KINDS, CUBE_ALIAS, IMAGE, NOISE_CUBE, isFilter, isPassKind, isWrap } from './types'
import type { ChannelSpec, ParsedSource, PassSpec } from './types'

/** 选 image pass 源码: 裸内容优先, 否则取最后一个 ```image 块; 都无则抛错 */
function pickImageCode(bare: string[], imageBlocks: string[]): string {
  const fromBare = bare.join('\n').trim()
  if (fromBare) return fromBare
  if (imageBlocks.length) return imageBlocks[imageBlocks.length - 1]
  throw new Error('着色器缺少 image pass: buffer 是离屏的不可见, 请提供未包裹的 mainImage 代码或 ```image 块')
}

/** 解析 ```lang 代码块; 未包裹的裸代码 = image pass */
export function parseShaderSource(src: string): ParsedSource {
  const lines = src.split('\n')
  const blocks: { lang: string; code: string[] }[] = []
  const bare: string[] = []
  let cur: { lang: string; code: string[] } | null = null
  let sawPassBlock = false // 是否已出现显式 pass 块(buffer/common/image 等)
  const fenceRe = /^\s*`{3,}[ \t\r]*(\w*)[ \t\r]*$/ // 支持 ``` / ```` 等 3 个以上反引号, 容忍空格/CR

  for (const ln of lines) {
    const m = ln.match(fenceRe)
    if (m) {
      if (cur) { blocks.push(cur); cur = null }
      else cur = { lang: m[1], code: [] }
      continue
    }
    if (cur) cur.code.push(ln)
    else bare.push(ln)
  }
  if (cur) blocks.push(cur)

  let common = ''
  const specs: PassSpec[] = []
  const imageBlocks: string[] = []
  for (const b of blocks) {
    if (!b.lang) { bare.push(...b.code); continue } // 无标签块 → 并入裸内容(image)
    const code = b.code.join('\n')
    if (b.lang === BLOCK_LANG.common) { common += '\n' + code; sawPassBlock = true }
    else if (b.lang === BLOCK_LANG.sound) { /* 音频 pass 不支持, 忽略 */ }
    else if (b.lang === BLOCK_LANG.glsl) { imageBlocks.push(code) } // ```glsl 围栏视作 image pass
    else if (isPassKind(b.lang)) {
      sawPassBlock = true
      if (b.lang === IMAGE) { imageBlocks.push(code) } // ```image 围栏
      else {
        const cnt = countMainImage(code)
        if (cnt > 1) {
          throw new Error(`${b.lang} pass 包含 ${cnt} 个 mainImage: 请用 \`\`\` 围栏把各 pass 分开`)
        }
        specs.push({ kind: b.lang, code, channels: parseChannels(code), w: parseRes(code, 'x'), h: parseRes(code, 'y') })
      }
    }
    else bare.push(...b.code) // 未知语言 → 并入裸内容
  }

  // image pass: 裸内容优先, 否则最后一个 ```image 块
  const imageCode = pickImageCode(bare, imageBlocks)

  // 裸代码含多个 mainImage 且无显式 pass 块 → 自动按 mainImage 拆分多 pass(多 pass 代码可直接裸粘)
  const imgCount = countMainImage(imageCode)
  if (imgCount > 1 && !sawPassBlock) {
    try {
      const r = splitRawPasses(imageCode)
      autoWireChannels(r.passes)
      return r
    } catch { /* 拆分失败 → 走下方引导报错 */ }
  }
  if (imgCount > 1) {
    throw new Error(
      `image pass 包含 ${imgCount} 个 mainImage: 若是多 pass 着色器, 请把各 pass 用 \`\`\`bufferA 等围栏分开, 仅留一个 mainImage 作为 image`
    )
  }
  specs.push({ kind: IMAGE, code: imageCode, channels: parseChannels(imageCode), w: 0, h: 0 })
  autoWireChannels(specs)
  return { common, passes: specs }
}

/**
 * 通道自动接线(仅填补未声明的通道, target === ''):
 * 多 buffer(≥2) 按 iChannelN→第N个buffer(覆盖 "iChannel0 is always Buf A" 类代码);
 * 单 buffer 仅 image.iChannel0 → 该 buffer(其余不擅自接, 分不清自反馈与噪点)。
 * 显式声明始终优先。
 */
function autoWireChannels(specs: PassSpec[]): void {
  const bufs = specs.filter(s => BUFFER_KINDS.includes(s.kind))
  if (bufs.length >= 2) {
    for (const s of specs) {
      for (let i = 0; i < 4; i++) {
        if (!s.channels[i].target && bufs[i]) s.channels[i] = { target: bufs[i].kind }
      }
    }
  } else if (bufs.length === 1) {
    const img = specs.find(s => s.kind === IMAGE)
    if (img && !img.channels[0].target) img.channels[0] = { target: bufs[0].kind }
  }
}

/**
 * 无围栏裸代码含多个 mainImage 时自动拆分: 前导代码 → common,
 * 其余按 mainImage 顺序 → bufferA..D, 最后一个 → image。
 * 两段 mainImage 间的辅助代码并入前后两 pass(不同 program 各自编译, 重复定义无碍)。
 */
function splitRawPasses(src: string): ParsedSource {
  const n = src.length
  const skip = (p: number): number => {
    if (src.startsWith('//', p)) {
      const nl = src.indexOf('\n', p)
      return nl === -1 ? n : nl + 1
    }
    if (src.startsWith('/*', p)) {
      const end = src.indexOf('*/', p + 2)
      return end === -1 ? n : end + 2
    }
    return p
  }

  const decls: number[] = [] // 顶层 mainImage 关键字位置
  const bodies: number[] = [] // 函数体 '{' 位置
  const ends: number[] = []   // 函数体 '}' 之后的位置
  let i = 0
  let depth = 0
  while (i < n) {
    const c = src[i]
    if (c === '/' && (src[i + 1] === '/' || src[i + 1] === '*')) { i = skip(i); continue }
    if (c === '{') depth++
    else if (c === '}') depth--
    else if (depth === 0 && src.startsWith('mainImage', i)) {
      let j = i + 9
      while (j < n && (src[j] === ' ' || src[j] === '\t' || src[j] === '\n' || src[j] === '\r')) j++
      if (src[j] === '(') {
        let k = j + 1
        let d = 1
        while (k < n && d > 0) {
          if (src[k] === '(') d++
          else if (src[k] === ')') d--
          k++
        }
        while (k < n && (src[k] === ' ' || src[k] === '\t' || src[k] === '\n' || src[k] === '\r')) k++
        if (src[k] === '{') {
          decls.push(i)
          bodies.push(k)
          let d2 = 1
          let p = k + 1
          while (p < n && d2 > 0) {
            if (src[p] === '/' && (src[p + 1] === '/' || src[p + 1] === '*')) { p = skip(p); continue }
            if (src[p] === '{') d2++
            else if (src[p] === '}') d2--
            p++
          }
          ends.push(p)
          i = p
          continue
        }
      }
    }
    i++
  }

  if (decls.length < 2) {
    throw new Error('自动拆分失败: 未找到 ≥2 个完整的 mainImage 函数体')
  }
  const names = BUFFER_KINDS
  if (decls.length - 1 > names.length) {
    throw new Error(`含 ${decls.length} 个 mainImage, 超过 4 个 buffer, 无法自动拆分`)
  }

  const common = src.slice(0, decls[0])
  const passes: PassSpec[] = []
  for (let s = 0; s < decls.length; s++) {
    // 前一段函数体结束 与 本段声明 之间的代码 → 并入本 pass(同时已在上一 pass 末尾)
    const between = s > 0 ? src.slice(ends[s - 1], decls[s]) : ''
    const body = src.slice(bodies[s], ends[s])
    const tail = s < decls.length - 1 ? src.slice(ends[s], decls[s + 1]) : src.slice(ends[s])
    const code = between + body + tail
    if (s < decls.length - 1) {
      passes.push({ kind: names[s], code, channels: parseChannels(code), w: parseRes(code, 'x'), h: parseRes(code, 'y') })
    } else {
      passes.push({ kind: IMAGE, code, channels: parseChannels(code), w: 0, h: 0 })
    }
  }

  // 通道自动接线由 parseShaderSource 统一调用 autoWireChannels 处理
  return { common, passes }
}

/** 统计代码中 mainImage 函数定义数(用于围栏识别诊断) */
function countMainImage(code: string): number {
  const m = code.match(/\bmainImage\s*\(/g)
  return m ? m.length : 0
}

/**
 * 解析 pass 内的 `// iChannelN: <目标> [filter] [wrap]` 接线注释。
 * 采样选项位置无关, 支持裸词(linear / mipmap / clamp / repeat)或键值(filter=x wrap=y)。
 */
function parseChannels(code: string): ChannelSpec[] {
  // '' = 未指定: 由 autoWireChannels / resolveChannel 兜底
  const ch: ChannelSpec[] = [0, 1, 2, 3].map(() => ({ target: '' }))
  for (let i = 0; i < 4; i++) {
    const m = code.match(new RegExp(`//\\s*iChannel${i}\\s*:\\s*([\\w.]+)\\s*([^\\r\\n]*)`))
    if (!m) continue
    const spec: ChannelSpec = { target: m[1] === CUBE_ALIAS ? NOISE_CUBE : m[1] }
    // 'cube' 是噪点 cubemap 别名, 其余按 pass 名由 resolver 动态匹配
    for (const tok of m[2].trim().split(/\s+/)) {
      if (!tok) continue
      const eq = tok.indexOf('=')
      const key = eq >= 0 ? tok.slice(0, eq) : tok
      const val = eq >= 0 ? tok.slice(eq + 1) : tok
      if (key === 'filter' && isFilter(val)) spec.filter = val
      else if (key === 'wrap' && isWrap(val)) spec.wrap = val
      else if (isFilter(tok)) spec.filter = tok
      else if (isWrap(tok)) spec.wrap = tok
    }
    ch[i] = spec
  }
  return ch
}

/** 解析 pass 内的 `// iResolution: WxH` 注释(0 = 跟随画布) */
function parseRes(code: string, axis: 'x' | 'y'): number {
  const m = code.match(/\/\/\s*iResolution\s*:\s*(\d+)\s*[xX]\s*(\d+)/)
  if (!m) return 0
  return axis === 'x' ? parseInt(m[1], 10) : parseInt(m[2], 10)
}

/** 管线缓存键 */
export function canonicalKey(common: string, passes: PassSpec[]): string {
  return common + '\u0000' + passes
    .map(p => p.kind + '|' + p.w + 'x' + p.h + '|' + p.channels.map(c =>
      c.target + (c.filter ? '~' + c.filter : '') + (c.wrap ? '=' + c.wrap : '')).join(',') + '|' + p.code)
    .join('\u0001')
}